import { Dispatch, forwardRef, PropsWithChildren, SetStateAction, useImperativeHandle, useRef, useState } from 'react'
import { tv } from 'tailwind-variants'
import { useAppDispatch, useAppSelector } from '~/store/hooks'
import { centerSelected, selectNumberCenterable } from '~/store/objects/centering'
import { Menu, MenuItem } from '~/ui/Menu'

type ContextMenuRef = Dispatch<SetStateAction<{ x: number; y: number } | null>>

const ContextMenu = forwardRef<ContextMenuRef>(function ContextMenu(_props, ref) {
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null)
  const menuParent = useRef<HTMLDivElement>(null!)
  const dispatch = useAppDispatch()

  useImperativeHandle(ref, () => setMenuPosition, [])

  return (
    <div
      ref={menuParent}
      style={{
        position: 'fixed',
        top: menuPosition?.y,
        left: menuPosition?.x,
        width: 1,
        height: 1,
      }}
      data-pressed={!!menuPosition || undefined}
    >
      <Menu
        aria-label="Placement"
        popoverProps={{
          triggerRef: menuParent,
          isOpen: !!menuPosition,
          onOpenChange: isOpen => {
            if (!isOpen) {
              setMenuPosition(null)
            }
          },
          placement: 'bottom left',
        }}
        onClose={() => {
          setMenuPosition(null)
        }}
      >
        <MenuItem onAction={() => dispatch(centerSelected('x'))}>Center Horizontally</MenuItem>
        <MenuItem onAction={() => dispatch(centerSelected('y'))}>Center Vertically</MenuItem>
      </Menu>
    </div>
  )
})

export const drawingCanvasContainerStyles = tv({
  base: 'w-full h-full',
  variants: {
    hasDockedPanel: {
      false: '',
      true: 'pr-[280px]',
    },
  },
})

export function DrawingCanvasContainer({ children, hasDockedPanel }: PropsWithChildren<{ hasDockedPanel: boolean }>) {
  const contextMenu = useRef<ContextMenuRef>(null!)
  const count = useAppSelector(state => selectNumberCenterable(state))
  const is3D = useAppSelector(state => state.camera.is3D)

  return (
    <div
      className={drawingCanvasContainerStyles({ hasDockedPanel })}
      onContextMenu={e => {
        if (e.target instanceof HTMLElement && e.target.getAttribute('aria-hidden')) {
          return
        }
        if (count <= 0 || is3D) {
          e.preventDefault()
          return
        }

        e.preventDefault()
        contextMenu.current({ x: e.pageX, y: e.pageY })
      }}
    >
      {children}
      <ContextMenu ref={contextMenu} />
    </div>
  )
}
